import * as React from "react";
import { X } from "lucide-react";
import { Badge } from "src/vibe-code/rcm-v2/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/vibe-code/rcm-v2/components/ui/command";
import { cn } from "src/vibe-code/rcm-v2/lib/utils";

interface MultiSelectProps {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select items...",
  className,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const handleUnselect = (item: string) => {
    onChange(selected.filter((s) => s !== item));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const input = event.target as HTMLInputElement;
    if (event.key === "Enter" && input.value.trim()) {
      event.preventDefault();
      const newValue = input.value.trim();
      if (!selected.includes(newValue)) {
        onChange([...selected, newValue]);
      }
      setInputValue("");
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Command className="overflow-visible bg-transparent">
        <div className="group border border-input px-3 py-2 text-sm ring-offset-background rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          <div className="flex gap-1 flex-wrap">
            {selected.map((item) => (
              <Badge key={item} variant="secondary">
                {item}
                <button
                  className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUnselect(item);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => handleUnselect(item)}
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            ))}
            <CommandInput
              value={inputValue}
              onValueChange={setInputValue}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              className="ml-2 bg-transparent outline-none placeholder:text-muted-foreground flex-1"
            />
          </div>
        </div>
        <div className="relative mt-2">
          {open && (
            <div className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
              <CommandList>
                <CommandEmpty>
                  {inputValue ? (
                    <div className="p-2 text-sm">
                      Press Enter to add "{inputValue}" as a custom lender
                    </div>
                  ) : (
                    "No lenders found."
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {options
                    .filter((option) => !selected.includes(option))
                    .map((option) => (
                      <CommandItem
                        key={option}
                        onMouseDown={(e) => e.preventDefault()}
                        onSelect={() => {
                          onChange([...selected, option]);
                          setInputValue("");
                        }}
                        className="cursor-pointer"
                      >
                        {option}
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </div>
          )}
        </div>
      </Command>
    </div>
  );
}
